/**
 * pilotEligibility
 * ----------------
 * Pilot eligibility rules for the school portal (TDI-488; pilot SOP v0.3
 * section 8 and Appendix G; age range confirmed by Tom King, 24 October 2026):
 *
 *   - Autism ('asd'): any year group, until 31 December in Year 11. No GP rule.
 *   - ADHD ('adhd'): Year 2 to Year 11, and the child must be registered with
 *     one of the approved pilot GP practices below.
 *   - Both: eligible only when the child is eligible for both pathways.
 *   - SEND pathway ('sen'): Key Stage 1 and 2 only (Year 1 to Year 6).
 *
 * The school portal reads these rules for its screens; rmsHarbourStartReferral
 * and rmsHarbourStartSenPathway enforce the same rules on the server. The
 * rules and the practice list are configuration here, not code in the
 * consumers, so a rule change during the pilot changes this file only.
 *
 * Mirrors python-functions/constants/rms_pilot_eligibility.py — keep in sync
 * (a parity test pins the two files to each other).
 */

// Appendix G: the approved pilot GP practices. ADHD and combined referrals
// need one; autism-only referrals and the SEND pathway have no GP rule. This
// list matches the pilot eligibility guide issued to schools.
export const APPROVED_GP_PRACTICES = [
  'Abbey Dale Medical Centre',
  'Adelaide Street Surgery',
  'Arnold Medical Centre',
  'Bloomfield Medical Centre',
  'Cleveleys Group Practice',
  'The Crescent Surgery',
  'Glenroyd Medical Centre',
  'Highfield Surgery',
  'Layton Medical Centre',
  'Marton Medical Practice',
  'Newton Drive Health Centre',
  'North Shore Surgery',
  'South King Street Medical Centre',
  'St Paul’s Medical Centre',
  'Stonyhill Medical Practice',
  'Waterloo Medical Centre',
];

// The two honest non-list answers the referral start offers ("Another
// practice", "I don't know yet"). Neither can proceed to an ADHD or combined
// referral; each gets its own explanation.
export const GP_PRACTICE_OTHER = 'other';
export const GP_PRACTICE_UNKNOWN = 'unknown';

// Appendix F, BTH pilot SOP v0.8 (TDI-776, R-042): the participating schools
// Register SENCO picks from, each spelt exactly as the SOP spells it. The
// name goes onto pupils, invites, Admin Handover's source organisation and
// the EMIS file names, so one spelling everywhere. A null phase is an
// all-through school (Armfield), where registration still asks the phase.
// Mirrors PARTICIPATING_SCHOOLS in
// python-functions/constants/rms_pilot_eligibility.py — keep in sync (the
// parity test pins the two files to each other).
export const PARTICIPATING_SCHOOLS = [
  { name: 'Anchorsholme Academy', phase: 'primary' },
  { name: 'Armfield Academy', phase: null },
  { name: 'Bispham Endowed CE Primary School', phase: 'primary' },
  { name: 'Blackpool Gateway Academy', phase: 'primary' },
  { name: 'Boundary Primary School', phase: 'primary' },
  { name: 'Hawes Side Academy', phase: 'primary' },
  { name: 'Holy Family Catholic Primary School', phase: 'primary' },
  { name: 'Kincraig Primary School', phase: 'primary' },
  { name: 'Layton Primary School', phase: 'primary' },
  { name: 'Montgomery Academy', phase: 'secondary' },
  { name: 'Moor Park Primary School', phase: 'primary' },
  { name: 'South Shore Academy', phase: 'secondary' },
  { name: 'St John Vianney Catholic Primary School', phase: 'primary' },
  { name: "St John's C of E Primary School", phase: 'primary' },
  { name: 'St Nicholas Primary School', phase: 'primary' },
  { name: 'Stanley Primary School', phase: 'primary' },
  { name: 'Waterloo Primary Academy', phase: 'primary' },
  { name: 'Westcliff Primary Academy', phase: 'primary' },
  { name: 'Westminster Primary Academy', phase: 'primary' },
];

/** The listed school's phase, or undefined for a name not on the list; null
 * means all-through (Armfield), where registration still asks the phase. */
export function participatingSchoolPhase(schoolName) {
  const found = PARTICIPATING_SCHOOLS.find((s) => s.name === String(schoolName || '').trim());
  return found ? found.phase : undefined;
}

// Year-group rules per pathway ('sen' is the SEND pathway). Year numbers count
// Reception as 0. finalYearUntil31December: the last eligible year group stays
// eligible only until 31 December of its school year (autism in Year 11).
export const PATHWAY_YEAR_RULES = {
  adhd: { minYear: 2, maxYear: 11, finalYearUntil31December: false, gpPracticeRequired: true },
  asd: { minYear: 0, maxYear: 11, finalYearUntil31December: true, gpPracticeRequired: false },
  sen: { minYear: 1, maxYear: 6, finalYearUntil31December: false, gpPracticeRequired: false },
};

/**
 * The year groups a school can add a pupil in: those some pilot pathway
 * takes (the span of PATHWAY_YEAR_RULES, Reception to Year 11), narrowed to
 * the school's phase when it is known (primary Reception to Year 6,
 * secondary Year 7 up). Ellen's eligibility flowchart, as the rules encode it.
 * @param {'primary'|'secondary'|null} [schoolPhase]
 * @returns {string[]}
 */
export function pilotYearGroups(schoolPhase) {
  const rules = Object.values(PATHWAY_YEAR_RULES);
  const min = Math.min(...rules.map((r) => r.minYear));
  const max = Math.max(...rules.map((r) => r.maxYear));
  const years = [];
  for (let y = min; y <= max; y += 1) {
    if (schoolPhase === 'primary' && y > 6) continue;
    if (schoolPhase === 'secondary' && y < 7) continue;
    years.push(y === 0 ? 'Reception' : `Year ${y}`);
  }
  return years;
}

/**
 * 'Reception' -> 0, 'Year 7' -> 7; null for anything else.
 * @param {string} [yearGroup]
 * @returns {number | null}
 */
export function yearNumberFromGroup(yearGroup) {
  if (yearGroup === 'Reception') return 0;
  const match = /^Year (\d{1,2})$/.exec(String(yearGroup || '').trim());
  if (!match) return null;
  const n = Number(match[1]);
  return n >= 1 && n <= 13 ? n : null;
}

/**
 * True when this pathway needs an approved pilot GP practice (ADHD).
 * @param {string} pathway
 * @returns {boolean}
 */
export function gpPracticeRequired(pathway) {
  const rules = PATHWAY_YEAR_RULES[String(pathway || '').trim().toLowerCase()];
  return Boolean(rules && rules.gpPracticeRequired);
}

/**
 * The year-group rule for one pathway.
 *
 * Reason codes: UNKNOWN_PATHWAY, YEAR_GROUP_MISSING (no usable year group —
 * fail closed), BELOW_MINIMUM_YEAR, ABOVE_MAXIMUM_YEAR, and
 * FINAL_YEAR_WINDOW_CLOSED (the 31 December cutoff in the final eligible year
 * has passed).
 *
 * @param {string} pathway - 'adhd' | 'asd' | 'sen'
 * @param {string} [yearGroup] - 'Reception' | 'Year 1'..'Year 13'
 * @param {Date} [today]
 * @returns {{ eligible: boolean, reasonCode: string | null }}
 */
export function evaluateYearGroup(pathway, yearGroup, today = new Date()) {
  const rules = PATHWAY_YEAR_RULES[String(pathway || '').trim().toLowerCase()];
  if (!rules) return { eligible: false, reasonCode: 'UNKNOWN_PATHWAY' };
  const year = yearNumberFromGroup(yearGroup);
  if (year === null) return { eligible: false, reasonCode: 'YEAR_GROUP_MISSING' };
  if (year < rules.minYear) return { eligible: false, reasonCode: 'BELOW_MINIMUM_YEAR' };
  if (year > rules.maxYear) return { eligible: false, reasonCode: 'ABOVE_MAXIMUM_YEAR' };
  if (year === rules.maxYear && rules.finalYearUntil31December) {
    // The school year runs September to August: September to December is
    // before the cutoff; January onwards in the same year group is past it.
    if (today.getMonth() < 8) return { eligible: false, reasonCode: 'FINAL_YEAR_WINDOW_CLOSED' };
  }
  return { eligible: true, reasonCode: null };
}

/**
 * The GP-practice rule for one pathway.
 *
 * Reason codes: GP_PRACTICE_MISSING (no answer given),
 * GP_PRACTICE_NOT_APPROVED ("Another practice", or anything not on the
 * approved list) and GP_PRACTICE_UNKNOWN ("I don't know yet").
 *
 * @param {string} pathway
 * @param {string} [gpPractice]
 * @returns {{ eligible: boolean, reasonCode: string | null }}
 */
export function evaluateGpPractice(pathway, gpPractice) {
  if (!gpPracticeRequired(pathway)) return { eligible: true, reasonCode: null };
  const value = typeof gpPractice === 'string' ? gpPractice.trim() : gpPractice;
  if (!value) return { eligible: false, reasonCode: 'GP_PRACTICE_MISSING' };
  if (value === GP_PRACTICE_UNKNOWN) return { eligible: false, reasonCode: 'GP_PRACTICE_UNKNOWN' };
  if (value === GP_PRACTICE_OTHER || !APPROVED_GP_PRACTICES.includes(value)) {
    return { eligible: false, reasonCode: 'GP_PRACTICE_NOT_APPROVED' };
  }
  return { eligible: true, reasonCode: null };
}
